/**
 * THE ROLES — the closed vocabulary both halves of the app name in words. Five at 0.1.209,
 * seven since migration 051 added `catalog_admin` and `self_service`.
 *
 * ⚠️ THIS TUPLE IS THE ONLY ROLE LIST IN THE CODE (0.1.506). `apps/api/src/auth/jwt.ts` and
 * `apps/api/src/db/session.ts` each kept a five-element copy that 051 never reached, so the API
 * refused the two new roles on
 * every request. Both now derive from here; the tests beside them walk every value of
 * `APP_ROLES`. Add a role by adding it HERE and in the enum migration — nowhere else.
 *
 * ⚠️ HOISTED HERE BY THE DRY RULE'S FIRST BULLET (a fact duplicated once is hoisted), and the
 * duplicate that forced it is an invitation: `users.tsx` has held these labels since the screen
 * was built, and 0.1.209's invitation mail has to tell somebody what they are being invited AS.
 * Two files that can disagree about what `data_entry` is called would disagree in the worst
 * possible place — the screen an admin picks the role on, and the message the invitee reads.
 *
 * ⚠️ IT LIVES IN `packages/shared` FOR THE SAME REASON `retour.ts` DOES, not for tidiness. The
 * API needs these at runtime to render mail; the web screen needs them to populate a picker.
 * Importing the web's copy into the API is impossible, and importing the API's `users.ts` into
 * the browser would pull `pg` and the whole server tree into the bundle — a runtime import is
 * not a type import.
 *
 * `packages/shared` SHIPS BUILT JAVASCRIPT (`dist`) — run `npm run build:shared` before
 * anything that consumes it.
 */
/**
 * ⚠️ THE ORDER IS THE PICKER'S AND IT IS DESCENDING BY POWER, which is also the order the
 * permission matrix in migration 005 reads in. It is not alphabetical and should not become so.
 */
export const APP_ROLES = [
    'admin',
    'staff',
    'catalog_admin',
    'student',
    'data_entry',
    'self_service',
    'view_only',
];
/** How each role reads to a person. The stored value is the key; this is never persisted. */
export const ROLE_LABELS = {
    admin: 'Administrator',
    staff: 'Staff',
    catalog_admin: 'Catalogue administrator',
    student: 'Student',
    data_entry: 'Data entry',
    self_service: 'Self-service',
    view_only: 'View only',
};
/**
 * What each role may actually do, in one line.
 *
 * ⚠️ THESE ARE DESCRIPTIONS OF THE 005 MATRIX, NOT THE MATRIX. Nothing here grants anything —
 * the capability checks in the database do — so a wrong sentence here is a wrong sentence, not
 * a wrong permission. Kept beside the labels anyway because the invitation mail and the role
 * picker both need to answer "what am I agreeing to be", and a role name alone does not.
 */
export const ROLE_SUMMARIES = {
    admin: 'Everything, including who has access, and the full export',
    staff: 'Bookings, clients, lenders, returns — the atelier’s operations',
    /** ⚠️ The summary names what it CANNOT do, because that is the distinction from `admin` and
     *  it is the reason this role exists (051). */
    catalog_admin: 'The whole catalogue — items, photos, categories, locations, fields. Not people, not deletions',
    student: 'Check-out, check-in, condition flags, intake',
    data_entry: 'Cataloguing: create items, photos, field values',
    /** ⚠️ "Their own" is load-bearing and is enforced in the database (051's ownership policies),
     *  not by this sentence. */
    self_service: 'Book garments for themselves — their own reservations only, and staff fulfil them',
    view_only: 'Read the catalogue, calendar and reports',
};
export function isAppRoleName(value) {
    return typeof value === 'string' && APP_ROLES.includes(value);
}
//# sourceMappingURL=roles.js.map